import { ICON_MANIFEST } from "../generated/iconManifest";

/**
 * PRE-BAKED ICONS. The shop, inventory, market and field guide show each plant, decor item, pot skin,
 * fish and insect as a small picture of its real art. Those pictures used to be produced at runtime —
 * plants/decor/pots by baking the procedural art to a texture and reading the pixels back, fish and insects
 * by rendering their SVG components inline in the DOM — so opening the Shop on a fresh session cost hundreds
 * of bakes, an inventory full of fish repainted dozens of gradient-heavy SVGs, and every reload paid it all
 * again.
 *
 * `scripts/bake-icons.mjs` now renders every icon once, at build time, into `public/icons/<kind>/<id>.png`
 * and lists them in `src/generated/iconManifest.ts`. At runtime an icon is a plain `<img>` URL the browser
 * fetches and caches like any asset (and, with `loading="lazy"`, only when it scrolls into view). The
 * runtime paths remain as the fallback for an id that is not in the manifest yet — a species added since
 * the last bake — and `npm run check` fails if the manifest is incomplete, so that fallback should never
 * run in production.
 */
/** `building` / `floor` / `shelf` are the Collection's greenhouse-skin previews (render/skinPreview.ts). */
export type IconKind = "decor" | "plant" | "pot" | "fish" | "insect" | "building" | "floor" | "shelf" | "fence";

const sets: Record<IconKind, Set<string>> = {
  decor: new Set(ICON_MANIFEST.decor),
  plant: new Set(ICON_MANIFEST.plant),
  pot: new Set(ICON_MANIFEST.pot),
  fish: new Set(ICON_MANIFEST.fish),
  insect: new Set(ICON_MANIFEST.insect),
  // the skin-preview kinds read through a fallback, so a manifest from before they existed still loads
  building: new Set((ICON_MANIFEST as { building?: string[] }).building ?? []),
  floor: new Set((ICON_MANIFEST as { floor?: string[] }).floor ?? []),
  shelf: new Set((ICON_MANIFEST as { shelf?: string[] }).shelf ?? []),
  fence: new Set((ICON_MANIFEST as { fence?: string[] }).fence ?? []),
};

/** The pre-baked PNG's URL for this icon, or null if it was not baked (caller falls back to the runtime path). */
export function prebakedIconUrl(kind: IconKind, id: string): string | null {
  return sets[kind].has(id) ? `/icons/${kind}/${id}.png` : null;
}
